import React from 'react';

import WorkflowArticleLayout from './components/WorkflowArticleLayout';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLearningPoints from './components/WorkflowLearningPoints';
import WorkflowList from './components/WorkflowList';
import WorkflowOutputBox from './components/WorkflowOutputBox';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowSection from './components/WorkflowSection';
import WorkflowStepFlow from './components/WorkflowStepFlow';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const beSteps = [
  {
    label: 'Part1',
    title: 'スキーマ定義とAPI仕様書を作成する',
    path: '/devdocs/workflow/be-step1',
  },
  { label: 'Part2', title: 'API実装ワークフローとその全体像', path: '/devdocs/workflow/be-step2' },
  {
    label: 'Part3',
    title: 'Phase1: DD書の作成',
    path: '/devdocs/workflow/be-step3',
    active: true,
  },
  { label: 'Part4', title: 'Phase2: コード生成', path: '/devdocs/workflow/be-step4' },
  { label: 'Part5', title: 'Phase3: 単体テスト', path: '/devdocs/workflow/be-step5' },
  { label: 'Part6', title: 'E2Eテスト', path: '/devdocs/workflow/be-step6' },
  { label: '付録', title: '成果物サンプル集', path: '/devdocs/workflow/be-appendix' },
];

const DIR_TREE = [
  {
    name: '{API_ID}',
    desc: 'API IDと同名のディレクトリ',
    color: '#1565c0',
    defaultOpen: true,
    children: [
      {
        name: '{version}',
        desc: '変更履歴の最新バージョン',
        defaultOpen: true,
        children: [
          { name: 'diff.md', desc: '変更内容の差分ドキュメント' },
          { name: 'instructions.md', desc: 'DD書作成の指示書' },
          { name: 'mk_instructions.md', desc: 'Phase2で作成' },
          { name: 'test.md', desc: 'Phase3で作成' },
        ],
      },
    ],
  },
];

const WorkflowBeStep3: React.FC = () => {
  return (
    <WorkflowArticleLayout
      title="バックエンド開発編 Part3 — api_project_rule Phase1: 設計書の作成"
      seriesName="バックエンド開発編"
      tags={['Backend', 'Design', 'DD書']}
      seriesSteps={beSteps}
      prevLink={{ label: 'Part2: API実装ワークフロー全体像', path: '/devdocs/workflow/be-step2' }}
      nextLink={{ label: 'Part4: Phase2 コード生成', path: '/devdocs/workflow/be-step4' }}
    >
      <WorkflowLearningPoints
        items={[
          'Phase1で diff.md → instructions.md → DD書 を作成する手順',
          'create_dd.md（DD書生成ルール）の全文と設計意図',
          'DD書の種類ごとの基本構造と記載ルール',
        ]}
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'Phase1 の全体フロー' },
          { id: 'section-2', label: 'Step 1-1: 作業ディレクトリの作成' },
          { id: 'section-3', label: 'Step 1-2: diff.md の作成' },
          { id: 'section-4', label: 'Step 1-3: instructions.md の作成' },
          { id: 'section-5', label: 'Step 1-4: ユーザー確認' },
          { id: 'section-6', label: 'Step 1-5: DD書の生成' },
          { id: 'section-7', label: 'Step 1-6〜1-12: 後続ステップ' },
          { id: 'section-8', label: '関連するステアリングファイル' },
          { id: 'section-9', label: '注意すべきこと' },
          { id: 'section-10', label: '成果物まとめ' },
          { id: 'section-11', label: 'ベストプラクティスとアンチパターン' },
        ]}
      />

      {/* セクション1: Phase1 の全体フロー */}
      <WorkflowSection title="Phase1 の全体フロー" id="section-1" number={1}>
        <WorkflowStepFlow
          steps={[
            { label: '1-1. 作業ディレクトリの作成' },
            { label: '1-2. diff.md の作成' },
            { label: '1-3. instructions.md の作成' },
            { label: '1-4. 【実行停止】ユーザー確認', highlight: true },
            { label: '1-5. DD書の生成' },
            { label: '1-6. 【実行停止】ユーザー確認', highlight: true },
            { label: '1-7. indexの確認' },
            { label: '1-8. instructionsの最終確認' },
            { label: '1-9. API一覧の更新' },
            { label: '1-10. 権限一覧・マイグレーションの更新' },
            { label: '1-11. ルールの更新' },
            { label: '1-12. 用語集の更新' },
            { label: '【Phase1完了】実行停止', highlight: true },
          ]}
        />
      </WorkflowSection>

      {/* セクション2: Step 1-1 作業ディレクトリの作成 */}
      <WorkflowSection title="Step 1-1: 作業ディレクトリの作成" id="section-2" number={2}>
        <WorkflowText>API仕様書ごとに作業ディレクトリを作成します。</WorkflowText>

        <WorkflowSection title="ディレクトリ構造" subNumber="2.1">
          <WorkflowDirectoryTree root=".kiro/specs/10_API/" entries={DIR_TREE} />
        </WorkflowSection>

        <WorkflowSection title="ルール" subNumber="2.2">
          <WorkflowList
            items={[
              '.kiro/specs に 10_API ディレクトリを作成（既存ならスキップ）',
              'API IDと同名のディレクトリを作成（例: API3001/）',
              'API仕様書の変更履歴の最新バージョンでサブディレクトリを作成（例: v1/）',
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション3: Step 1-2 diff.md の作成 */}
      <WorkflowSection title="Step 1-2: diff.md の作成" id="section-3" number={3}>
        <WorkflowText>当該API仕様書の変更内容をまとめた差分ドキュメントを作成します。</WorkflowText>

        <WorkflowSection title="新規作成の場合（v1）" subNumber="3.1">
          <WorkflowText>APIの概要を記載します。</WorkflowText>
          <WorkflowCodeBlock
            title="diff.md の例（新規作成）"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# API3001 パートナー登録API diff

## 概要

新規パートナーを登録するAPIを新規作成する。

## 変更点

- パートナー登録API（POST /v1/partner/create）を新規追加
- partnerテーブルへのINSERT処理
- UUID自動採番によるパートナーID発行`}
          />
        </WorkflowSection>

        <WorkflowSection title="修正の場合（v2以降）" subNumber="3.2">
          <WorkflowText>前回バージョンとの差分を確認して記載します。</WorkflowText>
          <WorkflowList
            items={[
              'develop ブランチとのgit diffで変更箇所を特定',
              '前回バージョンの diff.md / instructions.md を参照',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="連動処理" subNumber="3.3">
          <WorkflowText>API仕様書の変更履歴に diff.md へのリンクを追加します。</WorkflowText>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4: Step 1-3 instructions.md の作成 */}
      <WorkflowSection title="Step 1-3: instructions.md の作成" id="section-4" number={4}>
        <WorkflowText>
          DD書の作成指示書を生成します。何をどう作成/修正するかの一覧をまとめます。
        </WorkflowText>

        <WorkflowSection title="記載内容" subNumber="4.1">
          <WorkflowTable
            headers={['セクション', '内容']}
            rows={[
              ['作成するDD書の一覧', '新規作成/修正するクラスとDD書の対応'],
              ['修正内容の概要', '各DD書で何をどう変更するかの概要'],
              ['API仕様書の注釈反映', 'API仕様書内の note:info/warn/alert を指示に変換'],
              ['不足indexの確認結果', 'SQLに対してindexが必要な場合の追加指示'],
            ]}
          />
        </WorkflowSection>

        <WorkflowInfoBox type="important">
          複数APIを同時に指定した場合、instructions.md
          は代表1つにのみ詳細を記載します。他のAPIのディレクトリには代表 instructions.md
          へのリンクのみ記載します。これにより、読み込み順による新規/修正の混乱を防ぎます。
        </WorkflowInfoBox>

        <WorkflowTipBox title="なぜDD書を直接作らず instructions.md を挟むのか">
          DD書をいきなり生成すると、以下の問題が起きやすくなります。
          {'\n\n'}
          ・方針のズレが大量のDD書に波及する:
          1つのAPIで5〜10個のDD書が生成されるため、方針ミスに気づいた時点で全て作り直しになる
          {'\n'}
          ・修正の指示が曖昧になる: 「あのDD書のあの部分を直して」ではなく「instructions
          の方針を修正して再生成」の方が手戻りが少ない
          {'\n'}
          ・レビューの粒度が合わない: DD書1つ1つをレビューするより、instructions
          で全体方針を一度に確認した方が効率的
          {'\n\n'}
          instructions.md
          は「何を作るか・どう作るかの方針書」です。この方針に合意してからDD書を生成することで、手戻りの範囲を最小化しています。
          {'\n\n'}
          Phase2の mk_instructions.md
          も同じ考え方です。コードを直接生成する前に「何をどう実装するか」の方針を確認することで、大量のコード修正を防ぎます。
        </WorkflowTipBox>
      </WorkflowSection>

      {/* セクション5: Step 1-4 ユーザー確認 */}
      <WorkflowSection title="Step 1-4: ユーザー確認" id="section-5" number={5}>
        <WorkflowText>AIが停止し、ユーザーの確認を待ちます。</WorkflowText>

        <WorkflowTable
          headers={['入力', '意味', '結果']}
          rows={[
            ['0:問題なし', 'diff.md, instructions.md に問題なし', 'Step 1-5（DD書生成）に進む'],
            ['1:指摘あり', 'Rv指摘/注釈を記載した', '指摘を反映して 1-2 → 1-3 を再実施'],
          ]}
        />

        <WorkflowSection title="Rv指摘の書き方" subNumber="5.1">
          <WorkflowText>
            指摘・注釈の記法は要件定義編 Part3 で紹介しています。API仕様書や diff.md /
            instructions.md に直接指摘を書き込みます：
          </WorkflowText>
          <WorkflowCodeBlock
            title="Rv指摘の書き方"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`\`\`\`note:rv
この処理概要では○○のケースが考慮されていない。追加すること。
\`\`\`

\`\`\`note:info
○○の場合はXXXを考慮する必要がある。instructionsに追加すること。
\`\`\``}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6: Step 1-5 DD書の生成 */}
      <WorkflowSection title="Step 1-5: DD書の生成" id="section-6" number={6}>
        <WorkflowText>
          instructions.md を元に、DD書（詳細設計書）を docs/30_DD/ 配下に作成/修正します。
        </WorkflowText>

        <WorkflowSection title="DD書の種類と配置先" subNumber="6.1">
          <WorkflowTable
            headers={['DD書の種類', '配置先', '内容']}
            rows={[
              ['Model', 'docs/30_DD/10_model/', 'ドメインモデルのフィールド・メソッド定義'],
              ['Model/error', 'docs/30_DD/10_model/error/', 'ビジネス例外の定義'],
              ['Model/vo', 'docs/30_DD/10_model/vo/', 'バリューオブジェクトの定義'],
              ['Model/mastertype', 'docs/30_DD/10_model/mastertype/', 'マスタ区分モデルの定義'],
              ['UseCase', 'docs/30_DD/20_usecase/', 'ユースケースの処理フロー'],
              ['Service', 'docs/30_DD/30_service/', '共有ドメインサービス'],
              ['Repository', 'docs/30_DD/40_repository/', 'Interface + Impl の定義'],
              ['Mapper', 'docs/30_DD/40_repository/mapper/', 'SQL定義 + Entity定義'],
              ['Controller', 'docs/30_DD/50_controller/', 'エンドポイント定義'],
              ['Const', 'docs/30_DD/60_const/', '定数Enum定義'],
              ['Properties', 'docs/30_DD/90_properties/', 'プロパティクラス定義'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="DD書の基本構造" subNumber="6.2">
          <WorkflowText>全てのDD書は以下の共通構造を持ちます：</WorkflowText>
          <WorkflowCodeBlock
            title="DD書の基本構造"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# クラス名

| class name | layer | package |
|---|---|---|
| クラス名.java | レイヤー | パッケージ |

## components

| component |
|---|
| [依存クラス名](リンク) |

## field

| name | type |
|---|---|
| フィールド名 | 型 |

## methods

### #メソッド名

#### 引数

| name | type | description |
|---|---|---|
| 引数名 | 型 | 説明 |

#### 戻り値

| name | type | description |
|---|---|---|
| --- | 型 | 説明 |

#### throws

| reason | type | description |
|---|---|---|
| 例外が発生する理由 | クラス名 | |

#### 処理

1. 処理内容`}
          />
        </WorkflowSection>

        <WorkflowSection title="DD書作成ルール: create_dd.md の紹介" subNumber="6.3">
          <WorkflowText>
            DD書を作成・修正する際にAIが自動的に参照するルールファイルです。Front Matterは fileMatch
            で docs/30_DD/**/*.md を指定し、DD書ファイルの読み書き時に自動で読み込まれます。
          </WorkflowText>
          <WorkflowText>
            テンプレート構造だけでなく、各DD書の種類ごとの記載ルール・命名規則・処理セクションの書き方もSteeringに定義しておきます。
          </WorkflowText>
          <WorkflowCodeBlock
            title="create_dd.md 全文を見る"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# create_dd

docs/30_DD 配下のファイルの作成・変更時のルール。

## ファイルのフォーマット

docs/30_DD/00_template 配下のファイルを参考にする。

## DD書作成要否

### DD書が必要なパッケージ

| パッケージ | DD書配置先 | 備考 |
|---|---|---|
| domain.model | docs/30_DD/10_model/ | ドメインモデル |
| domain.model.mastertype | docs/30_DD/10_model/mastertype/ | マスタ区分モデル |
| domain.model.error | docs/30_DD/10_model/error/ | ビジネス例外 |
| domain.model.vo | docs/30_DD/10_model/vo/ | バリューオブジェクト |
| domain.usecase | docs/30_DD/20_usecase/ | ユースケース |
| domain.service | docs/30_DD/30_service/ | ドメインサービス |
| domain.repository + infra.repository | docs/30_DD/40_repository/ | リポジトリ（Interface+Impl） |
| infra.mapper | docs/30_DD/40_repository/mapper/ | マッパー（末尾にEntity定義） |
| web.controller | docs/30_DD/50_controller/ | コントローラー |
| domain.constant | docs/30_DD/60_const/ | 定数Enum |
| domain.properties | docs/30_DD/90_properties/ | プロパティクラス |

### DD書が不要なパッケージ

| パッケージ | 理由 |
|---|---|
| domain.core | 共通ユーティリティ。構造が単純でDD書不要 |
| web.core | フレームワーク設定クラス。DD書不要 |
| web.controller.dto | DTO。API仕様書で定義済み。DD書不要 |
| web.controller.dto.model | ネストDTO。API仕様書で定義済み。DD書不要 |
| infra.entity | エンティティ。Mapper DD書内に定義を記載 |

## 共通ルール

- class name表はH1タイトルの直下に記載する。
- components, field, methodsの見出しはH2とする。
- メソッド名はH3、メソッド内のセクション（引数/戻り値/処理/throws等）はH4とする。
- メソッドや特定セクションが存在しない場合は「なし」と記載する。

### メソッド名の可視性プレフィックス

- public: #メソッド名
- private: -メソッド名
- protected: +メソッド名

### 処理記載ルール

- 誰が見てもコードが書けるレベルに詳細な処理を記載する。
- componentsで指定したクラスのメソッドを呼び出す場合はリンクを記載する。
- DBアクセスが発生する場合はSQLを記載する。

## レイヤー別ルール

### 10_model（モデル）
- layer: domain
- package: jp.co.ardito.inhouse.kanzhong.domain.model
- 通常methodsは「なし」。ビジネスロジックを持つ場合のみメソッドを記載

### 20_usecase（ユースケース）
- layer: domain
- メソッド名は #usecase で固定。メソッドは1個のみ。
- 引数は body で固定。typeには「API仕様書参照」とリンクを記載。

### 30_service（サービス）
- layer: domain
- componentsには呼び出すService/Repository/Propertiesを記載

### 40_repository（リポジトリ）
- Interface=domain / Impl=infra
- メソッド命名: fetch / insert / bulkInsert / update / delete

### 40_repository/mapper（マッパー）
- layer: infra
- メソッド命名: find / insert / bulkInsert / update / delete
- 処理セクションにSQL全文を記載する（省略不可）
- ファイル末尾にEntity定義を記載

### 50_controller（コントローラー）
- layer: web
- メソッド固有セクション: エンドポイント / リクエストボディ / レスポンスボディ / 処理

### 60_const（定数Enum）
- layer: domain
- fieldは code(String) と name(String) の2つで固定
- #of メソッド（code→Enum変換）は全Enumで必須

### 90_properties（プロパティ）
- layer: domain
- プロパティマッピングセクションでプロパティキーとの対応を記載`}
          />

          <WorkflowInfoBox type="warning">
            このルールはプロジェクトによって変わります。上記は Java + MyBatis +
            ヘキサゴナルアーキテクチャ向けのルールです。技術スタックが異なれば変わります：
            {'\n\n'}
            ・TypeScript + Prisma の場合 — レイヤー構成やメソッド命名規則が変わる
            {'\n'}
            ・DDD + CQRS の場合 — Command/Query の分離が DD書構造に反映される
            {'\n\n'}
            自分のプロジェクトに合わせてSteeringのフォーマットをカスタマイズしてください。
          </WorkflowInfoBox>

          <WorkflowInfoBox type="info">
            実際に生成されたDD書のサンプルはバックエンド開発編 付録 —
            成果物サンプル集に掲載しています。
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション7: Step 1-6〜1-12 後続ステップ */}
      <WorkflowSection title="Step 1-6〜1-12: 後続ステップ" id="section-7" number={7}>
        <WorkflowSection title="Step 1-6: ユーザー確認" subNumber="7.1">
          <WorkflowText>
            DD書の内容を確認します。操作は Step 1-4 と同じプロトコル（0: / 1:）です。1:
            の場合は指摘を反映して Step 1-5 から再実施します。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="Step 1-7: indexの確認" subNumber="7.2">
          <WorkflowText>
            作成/修正されたMapper DD書内のSQLに対して、不足しているindexがないか確認します。
          </WorkflowText>
          <WorkflowText>不足があれば：</WorkflowText>
          <WorkflowList items={['スキーマ定義にindex追加', 'instructions.md に追記']} />
        </WorkflowSection>

        <WorkflowSection title="Step 1-8: instructions の最終確認" subNumber="7.3">
          <WorkflowText>
            DD書への直接指摘修正により、instructions.md との間に乖離が生じていないか確認します。
          </WorkflowText>
          <WorkflowTable
            headers={['チェック項目', '対応']}
            rows={[
              ['instructions の指示 ≠ DD書の内容', 'instructions.md をDD書に合わせて修正'],
              ['実行漏れの指示がある', '実施する（1-7のindex追加含む）'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="Step 1-9: API一覧の更新" subNumber="7.4">
          <WorkflowText>
            docs/20_BD/20_API一覧/API一覧.md に当該APIの情報を追加/更新します。
          </WorkflowText>
          <WorkflowText>
            記載項目: API ID, API名, Controller, UseCase, Model, Service,
            Repository（各DD書へのリンク付き）
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="Step 1-10: 権限一覧・マイグレーションの更新" subNumber="7.5">
          <WorkflowText>API仕様書に「権限」セクションがある場合：</WorkflowText>
          <WorkflowList
            variant="ordered"
            items={[
              '権限一覧.md の該当行を追加/修正',
              'Flywayマイグレーションファイルを作成',
              '権限一覧とDB状態の整合性を確認',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="Step 1-11: ルールの更新" subNumber="7.6">
          <WorkflowText>
            設計書Rv指摘.md の中でステアリングルールへの追加が必要な指摘（,rule 付き）を対応します。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="Step 1-12: 用語集の更新" subNumber="7.7">
          <WorkflowText>新しい物理名を使った場合は用語集.md に追記します。</WorkflowText>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション8: 関連するステアリングファイル */}
      <WorkflowSection title="関連するステアリングファイル" id="section-8" number={8}>
        <WorkflowTable
          headers={['ファイル', 'Phase1での役割']}
          rows={[
            ['api_project_rule.md', 'ワークフロー全体の進行ルール'],
            ['create_dd.md', 'DD書のフォーマット・記載ルール'],
            ['api_specs_rule.md', 'API仕様書のルール、権限連動'],
            ['create_schema.md', 'スキーマ定義のルール（index確認時）'],
            ['migration_rule.md', 'マイグレーションファイルのルール'],
            ['note_rule.md', 'Rv指摘・注釈の記法'],
            ['word_rule.md', '用語集との整合ルール'],
          ]}
        />
      </WorkflowSection>

      {/* セクション9: 注意すべきこと */}
      <WorkflowSection title="注意すべきこと" id="section-9" number={9}>
        <WorkflowTable
          headers={['注意点', '説明']}
          rows={[
            [
              'DD書の処理セクションは詳細に',
              '「誰が見てもコードが書ける」レベル。曖昧な記述はPhase2でのコード品質低下に直結',
            ],
            ['Mapper SQLは省略不可', 'SQL文は必ず全文記載する。WHERE条件やJOINの漏れに注意'],
            [
              'componentsリンクを正確に',
              'DD書間のリンクが正しくないとコード生成時に依存解決に失敗する',
            ],
            [
              '指摘は面倒でも書く',
              '気づいた問題は note:rv で書いておかないと、Phase2以降で手戻りになる',
            ],
            ['既存DD書との整合性', '既存クラスに追記する場合、既存メソッドとの整合性を確認する'],
          ]}
        />
      </WorkflowSection>

      {/* セクション10: 成果物まとめ */}
      <WorkflowSection title="成果物まとめ" id="section-10" number={10}>
        <WorkflowOutputBox>
          <WorkflowTable
            headers={['成果物', '配置先', '利用先']}
            rows={[
              ['diff.md', '.kiro/specs/10_API/{API_ID}/{ver}/', '変更履歴の記録'],
              ['instructions.md', '.kiro/specs/10_API/{API_ID}/{ver}/', 'DD書作成の指示書'],
              ['DD書（複数）', 'docs/30_DD/ 各サブディレクトリ', 'Phase2（コード生成）の入力'],
              ['API一覧（更新）', 'docs/20_BD/20_API一覧/API一覧.md', '全体俯瞰'],
              ['権限一覧（更新）', 'docs/20_BD/40_権限/権限一覧.md', '認可制御'],
              ['マイグレーション', 'src/main/resources/db/migration/', 'DB構築'],
            ]}
          />
        </WorkflowOutputBox>
      </WorkflowSection>

      {/* セクション11: ベストプラクティスとアンチパターン */}
      <WorkflowSection title="ベストプラクティスとアンチパターン" id="section-11" number={11}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: 'instructions.md で方針を合意してからDD書を生成する',
              effect:
                '方針ズレが5〜10個のDD書に波及するのを防ぐ。合意後の生成なら修正範囲が最小になる',
            },
            {
              practice: 'DD書の処理セクションは「コードが書ける」レベルに詳細化する',
              effect:
                'Phase2でのコード品質がDD書の精度に直結する。曖昧な「○○を行う」は避け、判定条件・呼び出し先・例外ケースを明示する',
            },
            {
              practice: 'Mapper SQLは全文を正確に記載する',
              effect:
                'SQLの省略や曖昧記述はPhase2で誤ったクエリ生成に直結する。WHERE条件・JOIN条件は特に注意',
            },
            {
              practice: 'componentsリンクを正確に張る',
              effect:
                'DD書間のリンクが正しいとAIが依存関係を正確に把握でき、コード生成時のimportミスが減る',
            },
            {
              practice: '既存DD書との整合性を確認してからメソッド追加する',
              effect:
                '既存メソッドと引数・戻り値の命名規則が揃い、Repository/Mapper層の一貫性が保たれる',
            },
            {
              practice: '指摘は面倒でも note:rv で記録に残す',
              effect: 'Rv指摘.md に記録が蓄積されることで同じミスの再発防止ルールが形成される',
            },
          ]}
        />

        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: 'instructions.md を確認せずDD書生成に進む',
              effect:
                '方針ズレが全DD書に波及し、大量の書き直しが発生する。Step 1-4 の停止ポイントで方針を確認・合意する',
            },
            {
              practice: 'DD書の処理を「〇〇する」程度で済ませる',
              effect:
                'Phase2でAIが処理を推測してコードを生成し、仕様と異なるロジックになる。条件分岐・ループ・例外ケース・呼び出し先メソッドを明記する',
            },
            {
              practice: 'Mapper SQLを部分省略する',
              effect:
                'WHERE条件やJOIN条件が欠落したコードが生成され、テストで初めてバグが発覚する。SQL全文を記載し、動的SQLの <if> 条件も明示する',
            },
            {
              practice: 'DD書を直接手修正してinstructionsとの乖離を放置する',
              effect:
                '次回修正時にAIがinstructionsを読んで古い方針でDD書を上書きする。Step 1-8 でinstructionsをDD書に合わせて更新する',
            },
            {
              practice: 'index確認をスキップする',
              effect:
                '本番環境でスロークエリが発生し、パフォーマンス問題として後から発覚する。Step 1-7 でMapper SQLに対するindex網羅を確認する',
            },
            {
              practice: 'API一覧・権限一覧の更新を後回しにする',
              effect:
                'リンク切れや権限不整合が蓄積し、後続APIの開発時に影響が波及する。Phase1完了前にStep 1-9〜1-10を確実に実施する',
            },
          ]}
        />
      </WorkflowSection>
    </WorkflowArticleLayout>
  );
};

export default WorkflowBeStep3;
